import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAddresses,
  addAddress,
  deleteAddress,
  setDefaultAddress,
  updateAddress,
} from "../../../services/address";
import toast from "react-hot-toast";
import { useAuth } from "../../../utils/AuthContext";

function useAddress() {
  const queryClient = useQueryClient();
  const { isLoggedIn, user } = useAuth();
  // Get all user addresses
  const {
    data: addresses,
    isLoading: isLoadingAddresses,
    error: addressesError,
    refetch: refetchAddresses,
  } = useQuery({
    queryKey: ["addresses"],
    queryFn: getAddresses,
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!isLoggedIn,
  });

  // Add address mutation
  const addAddressMutation = useMutation({
    mutationFn: addAddress,
    onSuccess: () => {
      queryClient.invalidateQueries(["addresses"]);
      queryClient.invalidateQueries(["defaultAddress"]);
      toast.success("Address added successfully");
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to add address";
      toast.error(errorMessage);
    },
  });

  // Update address mutation
  const updateAddressMutation = useMutation({
    mutationFn: ({ addressId, addressData }) =>
      updateAddress(addressId, addressData),
    onSuccess: () => {
      queryClient.invalidateQueries(["addresses"]);
      queryClient.invalidateQueries(["defaultAddress"]);
      toast.success("Address updated successfully");
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to update address";
      toast.error(errorMessage);
    },
  });

  // Delete address mutation
  const deleteAddressMutation = useMutation({
    mutationFn: deleteAddress,
    onSuccess: () => {
      queryClient.invalidateQueries(["addresses"]);
      queryClient.invalidateQueries(["defaultAddress"]);
      toast.success("Address deleted successfully");
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to delete address";
      toast.error(errorMessage);
    },
  });

  // Set default address mutation
  const setDefaultAddressMutation = useMutation({
    mutationFn: setDefaultAddress,
    onSuccess: () => {
      queryClient.invalidateQueries(["addresses"]);
      toast.success("Default address updated successfully");
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to set default address";
      toast.error(errorMessage);
    },
  });

  // Helper functions
  const addNewAddress = (addressData) => {
    addAddressMutation.mutate(addressData);
  };

  const updateExistingAddress = (addressId, addressData) => {
    updateAddressMutation.mutate({ addressId, addressData });
  };

  const deleteExistingAddress = (addressId) => {
    deleteAddressMutation.mutate(addressId);
  };

  const setAsDefault = (addressId) => {
    setDefaultAddressMutation.mutate(addressId);
  };

  // Computed values
  const hasAddresses = addresses && addresses.length > 0;
  const addressCount = addresses ? addresses.length : 0;

  // Loading states
  const isLoading =
    isLoadingAddresses ||
    addAddressMutation.isLoading ||
    updateAddressMutation.isLoading ||
    deleteAddressMutation.isLoading ||
    setDefaultAddressMutation.isLoading;

  return {
    // Data
    addresses: addresses || [],
    hasAddresses,
    addressCount,

    // Loading states
    isLoading,
    isLoadingAddresses,
    isAddingAddress: addAddressMutation.isLoading,
    isUpdatingAddress: updateAddressMutation.isLoading,
    isDeletingAddress: deleteAddressMutation.isLoading,
    isSettingDefault: setDefaultAddressMutation.isLoading,

    // Error states
    error: addressesError,

    // Actions
    addAddress: addNewAddress,
    updateAddress: updateExistingAddress,
    deleteAddress: deleteExistingAddress,
    setDefaultAddress: setAsDefault,
    refetch: refetchAddresses,
  };
}

export default useAddress;
